import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DonationIntent = () => {
  const navigate = useNavigate();
  const [donationData, setDonationData] = useState(null);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [otherReason, setOtherReason] = useState('');

  useEffect(() => {
    // Load donation data from previous step
    const savedData = sessionStorage.getItem('donationData');
    if (savedData) {
      setDonationData(JSON.parse(savedData));
    }

    // Initialize scripts if needed
    if (window.initPathioScripts) {
      window.initPathioScripts();
    }
  }, []);

  const donationIntents = [
    {
      id: 'health-education',
      title: 'Health Education',
      description: 'Support educational programs about diseases, wellness, and prevention',
      icon: '🎓'
    },
    {
      id: 'research',
      title: 'Medical Research',
      description: 'Fund research initiatives to advance medical knowledge and treatments',
      icon: '🔬'
    },
    {
      id: 'community-health',
      title: 'Community Health',
      description: 'Support local healthcare initiatives and community clinics',
      icon: '🏥'
    },
    {
      id: 'patient-support',
      title: 'Patient Support',
      description: 'Help patients access treatment and medication',
      icon: '🤝'
    },
    {
      id: 'public-awareness',
      title: 'Public Awareness',
      description: 'Fund campaigns to raise awareness about health issues',
      icon: '📢'
    },
    {
      id: 'disaster-relief',
      title: 'Disaster Relief',
      description: 'Help communities affected by health crises',
      icon: '🚨'
    },
    {
      id: 'women-children',
      title: 'Women & Children Health',
      description: 'Support maternal health and pediatric care programs',
      icon: '👨‍👩‍👧‍👦'
    },
    {
      id: 'general',
      title: 'General Support',
      description: 'Let us use your donation where it\'s needed most',
      icon: '💜'
    }
  ];

  const handleSelectIntent = (intentId) => {
    setSelectedIntent(intentId);
  };

  const handleNext = () => {
    if (!selectedIntent && !otherReason) {
      alert('Please select a donation purpose');
      return;
    }

    const updatedData = {
      ...donationData,
      intent: selectedIntent,
      otherReason: selectedIntent === 'other' ? otherReason : ''
    };

    sessionStorage.setItem('donationData', JSON.stringify(updatedData));
    navigate('/donations/interest-form');
  };

  const handleBack = () => {
    navigate('/donations/setup');
  };

  return (
    <>
      <Preloader />
      <Sidebar />
      <Header />

      <main>
        <section className="ul-breadcrumb ul-section-spacing">
          <div className="ul-container">
            <h2 className="ul-breadcrumb-title">Support Our Organization</h2>
            <ul className="ul-breadcrumb-nav">
              <li><a href="/">Home</a></li>
              <li><span className="separator"><i className="flaticon-right"></i></span></li>
              <li>Donations - Step 2</li>
            </ul>
          </div>
        </section>

        <section className="ul-donation-intent ul-section-spacing">
          <div className="ul-container">
            {/* Progress Indicator */}
            <div className="ul-donation-progress">
              <div className="ul-progress-steps">
                <div className="ul-progress-step completed">
                  <span className="step-number">✓</span>
                  <span className="step-label">Setup</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step active">
                  <span className="step-number">2</span>
                  <span className="step-label">Intent</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">3</span>
                  <span className="step-label">Preferences</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">4</span>
                  <span className="step-label">Payment</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">5</span>
                  <span className="step-label">Confirm</span>
                </div>
              </div>
            </div>

            <div className="ul-donation-form-wrapper">
              <div className="ul-donation-intent-content">
                <h2>Step 2: What is Your Donation Purpose?</h2>
                <p className="ul-subtitle">Your donation will help us focus on the areas that matter most to you.</p>

                <div className="ul-intent-grid">
                  {donationIntents.map(intent => (
                    <div
                      key={intent.id}
                      className={`ul-intent-card ${selectedIntent === intent.id ? 'active' : ''}`}
                      onClick={() => handleSelectIntent(intent.id)}
                    >
                      <div className="ul-intent-icon">{intent.icon}</div>
                      <div className="ul-intent-content">
                        <h3>{intent.title}</h3>
                        <p>{intent.description}</p>
                      </div>
                      <div className="ul-intent-checkbox">
                        <input
                          type="radio"
                          name="intent"
                          value={intent.id}
                          checked={selectedIntent === intent.id}
                          onChange={() => handleSelectIntent(intent.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {selectedIntent === 'other' && (
                  <div className="ul-form-group">
                    <label className="ul-form-label">Please describe your reason:</label>
                    <textarea
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder="Tell us your specific donation purpose..."
                      className="ul-form-textarea"
                      rows="4"
                    />
                  </div>
                )}

                {/* Form Actions */}
                <div className="ul-form-actions">
                  <button 
                    type="button"
                    className="ul-btn-secondary"
                    onClick={handleBack}
                  >
                    ← Back
                  </button>
                  <button 
                    type="button"
                    className="ul-btn ul-btn-primary"
                    onClick={handleNext}
                  >
                    Next Step →
                  </button>
                </div>
              </div>

              {/* Summary Sidebar */}
              <div className="ul-donation-summary">
                <div className="ul-summary-card">
                  <h3>Donation Summary</h3>
                  {donationData && (
                    <>
                      <div className="ul-summary-item">
                        <span>Amount:</span>
                        <strong>${donationData.amount}</strong>
                      </div>
                      <div className="ul-summary-item">
                        <span>Donor:</span>
                        <strong>{donationData.name}</strong>
                      </div>
                      <div className="ul-summary-item">
                        <span>Email:</span>
                        <strong>{donationData.email}</strong>
                      </div>
                      <div className="ul-summary-divider"></div>
                      {selectedIntent && (
                        <div className="ul-summary-item">
                          <span>Purpose:</span>
                          <strong>
                            {donationIntents.find(i => i.id === selectedIntent)?.title}
                          </strong>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="ul-help-card">
                  <h3>Need Help?</h3>
                  <p>Not sure which option to choose? Our team can help you find the perfect cause.</p>
                  <a href="mailto:support@pathio.org" className="ul-help-link">
                    Contact Us →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DonationIntent;
